import React, { useState, useEffect } from "react";
import FormField from "@/components/common/FormField";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { errorTypes } from "@/utility/validation";
import type { OnboardTabProps } from "./onboard.types";
import { API_URL } from "@/api/constant";
import { useFormContext } from "react-hook-form";
import PageLoader from "@/components/common/PageLoader";
import { FileText, Eye } from "lucide-react";
import {
  validateFileType,
  ALLOWED_PHOTO_PDF_ACCEPT,
} from "@/utility/helper";
import FilePreviewModal from "@/components/common/FilePreviewModal";

const OnboardBasicDetail: React.FC<OnboardTabProps> = ({
  isLoading,
  isProfile,
  empId,
  handleNextStep,
  uploadFile,
  isGuestOrLevel5
}) => {
  const methods = useFormContext();
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = methods;

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    // Default user type to 0 (Staff)
    if (!watch("type")) {
      setValue("type", "0");
    }
  }, [setValue, watch]);

  const handleFileChange = async (e: any) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!validateFileType(file)) {
      input.value = "";
      return;
    }

    if (uploadFile) {
      const path = await uploadFile(file);
      if (path) {
        setValue("profilePhoto", path, { shouldValidate: true });
      }
    }
  };

  const currentPhoto = watch("profilePhoto");
  const isPdf = typeof currentPhoto === "string" && currentPhoto.toLowerCase().endsWith(".pdf");

  return (
    <>
      {isLoading == true ? <PageLoader /> : (
        <div>
          <div className="mb-4">
            {currentPhoto ? (
              <div className="flex items-center gap-4">
                <div
                  onClick={() => setIsPreviewOpen(true)}
                  className="cursor-pointer group relative overflow-hidden rounded-2xl border-2 border-primary/20 shadow-md group-hover:opacity-90 transition-opacity"
                  title="Click to preview"
                >
                  {isPdf ? (
                    <div className="w-20 h-20 bg-rose-50 border-2 border-rose-200 flex flex-col items-center justify-center shadow-md text-rose-600 group-hover:border-rose-400 transition-colors">
                      <FileText className="w-8 h-8" />
                      <span className="text-[10px] font-bold mt-1">PDF File</span>
                    </div>
                  ) : (
                    <img
                      src={(API_URL + "/" + currentPhoto) as string}
                      alt="Profile Preview"
                      className="w-20 h-20 object-cover "
                    />
                  )}
                  <div className="absolute inset-0 bg-black/30 rounded-2xl opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                    <Eye className="w-5 h-5" />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm text-green-600 font-medium">
                    {isPdf ? "PDF uploaded successfully" : "Image uploaded successfully"}
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setValue("profilePhoto", "", { shouldValidate: true })}
                    className="w-fit"
                  >
                    Remove Image
                  </Button>
                </div>
              </div>
            ) : (
              <FormField
                onChange={handleFileChange}
                type="file"
                name="profilePhoto"
                label="Upload Image / PDF (JPG, PDF only)"
                accept={ALLOWED_PHOTO_PDF_ACCEPT}
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              {...register("employeeCode", {
                ...errorTypes.required("Employee Code"),
              })}
              required
              name="employeeCode"
              label="Employee Code"
              type="text"
              maxLength={4}
              disabled={isGuestOrLevel5}
              placeholder="Enter employee code"
              error={errors.employeeCode?.message as string}
            />
            <FormField
              {...register("name", {
                ...errorTypes.required("Name"),
                ...errorTypes.name,
              })}
              required
              name="name"
              label="Full Name (As Per Aadhar)"
              type="text"
              maxLength={30}
              placeholder="Enter full name"
              error={errors.name?.message as string}
            />

            <FormField
              {...register("gender", { ...errorTypes.required("Gender") })}
              required
              name="gender"
              label="Gender"
              type="select"
              value={watch("gender")}
              placeholder="Select gender"
              options={[
                { value: "select gender", label: "Select gender" },
                { value: "0", label: "Male" },
                { value: "1", label: "Female" },
                { value: "2", label: "Other" },
              ]}
              error={errors.gender?.message as string}
            />
            <FormField
              {...register("isMarried", {
                ...errorTypes.required("Marital Status"),
              })}
              required
              name="isMarried"
              label="Marital Status"
              type="select"
              value={watch("isMarried")}
              options={[
                { value: "select marital status", label: "Select marital status" },
                { value: "1", label: "Married" },
                { value: "0", label: "Unmarried" },
                { value: "2", label: "Divorce" },
                { value: "3", label: "Widower" },
                { value: "4", label: "Widow" },

              ]}
              error={errors.isMarried?.message as string}
            />

            {watch("isMarried") === "1" && (
              <>
                <FormField
                  {...register("spouse", {
                    ...errorTypes.required("Spouse Full Name"),
                    ...errorTypes.name,
                  })}
                  required
                  name="spouse"
                  label="Spouse Full Name"
                  type="text"
                  maxLength={30}
                  placeholder="Enter spouse full name"
                  error={errors.spouse?.message as string}
                />
                <FormField
                  {...register("children", { ...errorTypes.required("Number of Children"), ...errorTypes.onlyNumbers })}
                  name="children"
                  label="Number of Children"
                  type="text"
                  placeholder="Enter number of children"
                  minLength={0}
                  maxLength={2}
                  pattern="[0-9]{1,2}"
                  required
                  error={errors.children?.message as string}
                />
              </>
            )}
            <FormField
              {...register("bloodGroup", { ...errorTypes.required("Blood Group") })}
              required
              name="bloodGroup"
              label="Blood Group"
              type="select"
              value={watch("bloodGroup")}
              placeholder="Select Blood Group"
              options={[
                { value: "", label: "Select Blood Group" },
                { value: "O+", label: "O+" },
                { value: "O-", label: "O-" },
                { value: "A+", label: "A+" },
                { value: "A-", label: "A-" },
                { value: "B+", label: "B+" },
                { value: "B-", label: "B-" },
                { value: "AB+", label: "AB+" },
                { value: "AB-", label: "AB-" },
              ]}
              error={errors.bloodGroup?.message as string}
            />
            {/* <FormField
              {...register("email", { ...errorTypes.required("Email"), ...errorTypes?.email })}
              required
              name="email"
              label=" Email"
              type="email"
              placeholder="Enter email"
              disabled={isProfile || isGuestOrLevel5}
              error={errors.email?.message as string}
            /> */}
            <FormField
              {...register("email", { ...errorTypes.required("Email"), ...errorTypes?.email })}
              required
              name="email"
              label=" Personal Email"
              type="email"
              placeholder="Enter Personal Email"
              disabled={isProfile || isGuestOrLevel5}
              error={errors.email?.message as string}
            />
            <FormField
              {...register("phone", { ...errorTypes.required("Phone"), ...errorTypes.phone })}
              required
              name="phone"
              label=" Primary Mobile No."
              type="text"
              placeholder="Enter Primary mobile No."
              disabled={isProfile || isGuestOrLevel5}
              minLength={10}
              pattern="[0-9]{10}"
              maxLength={10}
              error={errors.phone?.message as string}
            />
            <FormField
              {...register("secondaryPhone", { ...errorTypes.required("Secondary Phone"), ...errorTypes.phone })}
              required
              name="secondaryPhone"
              label=" Secondary Mobile No."
              type="text"
              placeholder="Enter Secondary mobile No."
              disabled={isProfile || isGuestOrLevel5}
              minLength={10}
              pattern="[0-9]{10}"
              maxLength={10}
              error={errors.secondaryPhone?.message as string}
            />
            <FormField
              {...register("dob", {
                ...errorTypes.required("Date of Birth"),
                ...errorTypes.dob,
              })}
              required
              name="dob"
              label="Date of Birth"
              type="date"
              placeholder="Enter Date of Birth"
              disabled={isProfile || isGuestOrLevel5}
              maxLength={10}
              error={errors.dob?.message as string}
            />
            <FormField
              {...register("fatherName", {
                ...errorTypes.required("Father Name"),
                ...errorTypes.name,
              })}
              required
              name="fatherName"
              label="Father Full Name"
              type="text"
              placeholder="Enter Father Full name"
              // disabled={isProfile}
              maxLength={30}
              error={errors.fatherName?.message as string}
            />
            <FormField
              {...register("motherName", {
                ...errorTypes.required("Mother Name"),
                ...errorTypes.name,
              })}
              required
              name="motherName"
              label="Mother Full Name"
              type="text"
              placeholder="Enter Mother Full name"
              // disabled={isProfile}
              maxLength={30}
              error={errors.motherName?.message as string}
            />
            <FormField
              type='select'
              label=" Emergency Contact Person (E.C.P) Relation"
              value={watch('ecpRelation')}
              {...register('ecpRelation', {
                required: 'This field is Required',
              })}
              required
              error={errors?.ecpRelation?.message as string}
              placeholder="Select Relation Type"
              options={[
                { value: 'Father', label: 'Father' },
                { value: 'Mother', label: 'Mother' },
                { value: "Spouse", label: "Spouse" },
                // { value: 'Wife', label: 'Wife' },
                // { value: 'Husband', label: 'Husband' },
                { value: 'Brother', label: 'Brother' },
                { value: 'Sister', label: 'Sister' },
                // { value: 'Other', label: 'Other' },
              ]}
            />
            <FormField
              {...register("ecpName", {
                ...errorTypes.required("Emergency Person"),
                ...errorTypes.name,
              })}
              required
              name="ecpName"
              label="Emergency Person Full Name"
              type="text"
              placeholder="Enter emergency person name"
              // disabled={isProfile}
              maxLength={60}
              error={errors.ecpName?.message as string}
            />
            <FormField
              {...register("ecpPhone", {
                ...errorTypes.required("Emergency Contact"),
                ...errorTypes.phone,
              })}
              required
              name="ecpPhone"
              label="Emergency Contact"
              type="text"
              placeholder="Enter emergency contact"
              // disabled={isProfile}
              minLength={10}
              pattern="[0-9]{10}"
              maxLength={10}
              error={errors.ecpPhone?.message as string}
            />
            <FormField
              {...register("joiningDate", {
                ...errorTypes.required("Joining Date"),
                ...errorTypes.joiningDate,
              })}
              required
              name="joiningDate"
              label="Joining Date"
              type="date"
              placeholder="Enter joining date"
              disabled={isProfile || isGuestOrLevel5}
              maxLength={10}
              max={new Date().toISOString().split("T")[0]}
              error={errors.joiningDate?.message as string}
            />


            {/* {
              !isGuestOrLevel5 && (
                <FormField
                  {...register("category", { ...errorTypes.required("Category") })}
                  required
                  name="category"
                  label="Employee Category"
                  type="select"
                  value={watch("category")}
                  options={[
                    { value: "", label: "Select category" },
                    { value: "Trainee", label: "Trainee" },
                    { value: "Monthly-Salary", label: "Monthly‑Salary" },
                    { value: "Consultant", label: "Consultant" },
                    { value: "Contract", label: "Contract" },
                    { value: "Daily-Wage", label: "Daily‑Wage" },
                  ]}
                  error={errors.category?.message as string}
                />
              )} */}



          </div>
          <div className="flex items-center justify-end mt-6">
            <Button
              type="button"
              disabled={isLoading}
              onClick={() => {
                const isMarried = watch("isMarried") === "1";
                const fields = [
                  "profilePhoto",
                  "employeeCode",
                  "name",
                  "gender",
                  "bloodGroup",
                  "email",
                  "phone",
                  "dob",
                  "fatherName",
                  "motherName",
                  "ecpName",
                  "ecpPhone",
                  "joiningDate",
                  "status",
                  "isMarried",
                  ...(isMarried ? ["spouse", "children"] : []),
                  "category",
                  "teamLeadId",
                  "isTeamLead",
                ];
                setValue("category", "Monthly-Salary");
                setValue("type", "0");
                handleNextStep("basic_details", "tab2", fields as any);
              }}
            >
              {isLoading ? (
                <Spinner />
              ) : empId ? (
                "Update and Continue"
              ) : (
                "Save and Continue"
              )}
            </Button>
          </div>

          <FilePreviewModal
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            fileUrl={currentPhoto}
            title={isPdf ? "Profile Document (PDF)" : "Profile Photo"}
          />
        </div>
      )
      }
    </>
  );
};

export default OnboardBasicDetail;
